import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Leaf, Users, ShieldCheck, ShoppingCart, Sparkles } from 'lucide-react';
import { useAuth } from 'lib/auth';
import { WebLayout } from 'components/Layout';
import { Button } from 'components/Elements';

export const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleStart = () => {
    user ? navigate('/app') : navigate('/auth/login');
  };

  return (
    <WebLayout>
      {/* HERO */}
      <section className="relative min-h-[85vh] flex flex-col items-center justify-center px-6 text-center overflow-hidden">
        {/* BACKGROUND SOFT GRADIENT */}
        <div className="absolute inset-0 bg-gradient-to-b from-white via-fundo-green-50/40 to-fundo-green-100/30 dark:from-gray-900 dark:via-gray-900/90 dark:to-gray-900"></div>

        {/* Decorative Glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-fundo-green-300/20 dark:bg-fundo-green-600/10 blur-3xl rounded-full"></div>

        {/* HERO TEXT */}
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative z-10 max-w-4xl"
        >
          <h1 className="text-4xl md:text-6xl font-extrabold leading-tight tracking-tight text-gray-900 dark:text-white">
            Powering the Next Generation of
            <br />
            <span className="text-fundo-green-700 dark:text-fundo-green-400">African Farming</span>
          </h1>

          <p className="mt-6 text-gray-600 dark:text-gray-300 text-lg md:text-xl max-w-2xl mx-auto">
            Access capital, tools, and markets built for Africa’s farmers. When farms rise, entire
            communities thrive.
          </p>

          <Button onClick={handleStart} size="lg" className="mx-auto mt-6">
            {user ? 'Go to Dashboard' : 'Get Started'}
          </Button>
        </motion.div>

        {/* Floating icons */}
        <Leaf
          className="absolute top-10 left-6 opacity-30 dark:opacity-20 text-fundo-green-300 dark:text-fundo-green-700 animate-pulse"
          size={52}
        />
        <Users
          className="absolute bottom-10 right-6 opacity-30 dark:opacity-20 text-fundo-green-300 dark:text-fundo-green-700 animate-pulse"
          size={52}
        />
      </section>

      {/* VALUE PROPOSITIONS */}
      <section className="mt-24 px-6 max-w-7xl mx-auto">
        <h2 className="text-center text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
          Grow Your Farming Business
        </h2>

        <p className="text-center mt-4 text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Fundo connects African farmers to financing, digital tools, and a trusted open market —
          all in one place.
        </p>

        <div className="mt-14 grid md:grid-cols-3 gap-10">
          {/* CARD TEMPLATE */}
          {[
            {
              icon: Users,
              title: 'Community Funding',
              text: `Farmers post real funding needs. Supporters contribute directly. Fundo delivers full transparency.`,
            },
            {
              icon: ShieldCheck,
              title: 'Smart Farm Tools',
              text: `Track livestock, crops, expenses, and sales using Fundo apps, WhatsApp, or USSD — anywhere.`,
            },
            {
              icon: ShoppingCart,
              title: 'Direct Market Access',
              text: `Connect instantly with buyers, cooperatives, and suppliers. Sell directly — no middlemen.`,
            },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={i}
                whileHover={{ y: -6 }}
                transition={{ duration: 0.2 }}
                className="p-8 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition cursor-pointer"
              >
                <div className="w-14 h-14 rounded-xl bg-fundo-green-100 dark:bg-fundo-green-900 text-fundo-green-700 dark:text-fundo-green-300 flex items-center justify-center mb-4">
                  <Icon size={28} />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                  {item.title}
                </h3>
                <p className="mt-2 text-gray-500 dark:text-gray-400">{item.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="mt-24 mb-24 text-center px-6">
        <div className="max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white"
          >
            Support a Farm. Empower a Family.
            <br />
            Grow a Nation.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mt-4 text-gray-600 dark:text-gray-400 text-lg"
          >
            Fundo unlocks opportunities for smallholder farms by combining community funding, smart
            tools, and transparent marketplaces.
          </motion.p>

          <Button size="lg" className="mx-auto mt-6" onClick={() => navigate('/request-for-demo')}>
            Request a Demo
          </Button>
        </div>
      </section>
    </WebLayout>
  );
};
